import { useState } from 'react';
import {
  Calendar,
  DollarSign,
  Droplets,
  Gauge,
  Route,
  Scale,
  Sparkles,
} from 'lucide-react';
import { MODEL_COLORS, RATE_MODELS } from './mockTransportData';

const ICON_MAP = {
  route: Route,
  gauge: Gauge,
  weight: Scale,
  droplets: Droplets,
  calendar: Calendar,
};

function RateRow({ rate, colors }) {
  return (
    <div className="flex items-center justify-between border-b border-logi-grid/30 px-4 py-2.5 last:border-0 hover:bg-logi-silver">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-logi-text">{rate.label}</p>
        <p className="text-[10px] text-logi-text0">Client: {rate.client}</p>
      </div>
      <div className="ml-3 text-right">
        <p className={`font-mono text-base font-bold tabular-nums ${colors.text}`}>
          {typeof rate.value === 'number' && rate.value >= 1000
            ? rate.value.toLocaleString()
            : rate.value}
        </p>
        <p className="text-[10px] text-logi-text0">{rate.unit}</p>
      </div>
    </div>
  );
}

function RateModelPanel({ model, isExpanded, onToggle }) {
  const Icon = ICON_MAP[model.icon];
  const colors = MODEL_COLORS[model.color];

  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white transition-all ${colors.border}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-white ${colors.bg}`}
      >
        <div
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ring-1 ${colors.bg} ${colors.ring}`}
        >
          <Icon className={`h-4 w-4 ${colors.text}`} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-logi-text">{model.label}</p>
          <p className="text-[11px] text-logi-text0">{model.description}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${colors.text} ${colors.bg} ring-1 ${colors.ring}`}>
          {model.rates.length} rates
        </span>
      </button>

      {isExpanded && (
        <div className="border-t border-logi-grid/80 bg-white">
          {model.rates.map((rate) => (
            <RateRow key={rate.id} rate={rate} colors={colors} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function RateCardEngine({ models = RATE_MODELS }) {
  const [expandedId, setExpandedId] = useState('per_trip');

  return (
    <section className="overflow-hidden rounded-xl border border-logi-grid bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-4 py-3">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-emerald-700" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Commercial Rate Card Engine</h3>
            <p className="text-[11px] text-logi-text0">
              UAE Oil &amp; Gas billing formats · Interactive pricing matrix
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-500/25">
          <Sparkles className="h-3 w-3" />
          5 Models Active
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-2">
        {models.map((model) => (
          <RateModelPanel
            key={model.id}
            model={model}
            isExpanded={expandedId === model.id}
            onToggle={() => setExpandedId(expandedId === model.id ? null : model.id)}
          />
        ))}
      </div>

      {/* Quick reference strip */}
      <div className="border-t border-logi-grid/80 bg-white px-4 py-3">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
          Blueprint Quick Reference
        </p>
        <div className="flex flex-wrap gap-2 text-[10px]">
          {[
            'Per Trip → Route legs',
            'Per KM → GPS validated',
            'Per MT → Chemical/Bitumen',
            'Per KL → Fuel/Gas Oil',
            'Fixed Monthly → Dedicated fleet',
          ].map((tip) => (
            <span
              key={tip}
              className="rounded-md bg-white px-2 py-1 text-logi-muted ring-1 ring-logi-grid"
            >
              {tip}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
